import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

const AdminLayout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { name: 'Demo Bookings', path: '/admin/demos', icon: '🎯' },
    { name: 'Manage Jobs', path: '/admin/jobs', icon: '💼' },
    { name: 'Job Applications', path: '/admin/applications', icon: '📩' },
    { name: 'Contact Enquiries', path: '/admin/enquiries', icon: '💬' },
    { name: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    navigate('/admin-login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex min-h-screen bg-slate-100 font-sans">
      
      {/* SIDEBAR - Desktop */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
        md:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="p-6 border-b border-slate-800 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">GEPRC <span className="text-blue-500">Admin</span></h1>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">Management Portal</p>
            </div>
            {/* Close button for mobile */}
            <button className="md:hidden text-slate-400" onClick={() => setIsMobileMenuOpen(false)}>
              ✕
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-grow p-4 space-y-1.5 overflow-y-auto">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20 font-bold'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span className="font-medium text-sm">{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* User & Logout Section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center space-x-3 px-4 py-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center font-bold text-xs">
                AD
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-medium truncate">Administrator</p>
                <p className="text-xs text-slate-500 truncate">globaleprc@gmail.com</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <span>🚪</span>
              <span className="font-medium text-sm">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden" 
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* MAIN CONTENT AREA */}
      <div className="flex-grow md:ml-64 flex flex-col min-h-screen">
        
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 sticky top-0 z-30">
          <div className="flex items-center space-x-4">
            <button 
              className="p-2 rounded-lg bg-slate-100 md:hidden text-slate-600"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              ☰
            </button>
            <h2 className="text-lg font-semibold text-slate-800 capitalize">
              {location.pathname.split('/').pop().replace('-', ' ')}
            </h2>
          </div>

          <div className="flex items-center space-x-4">
            <Link to="/" className="text-sm text-blue-600 hover:underline flex items-center space-x-1">
              <span>🌐</span>
              <span className="hidden sm:inline">View Website</span>
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-8 flex-grow">
          <div className="animate-fadeIn">
            <Outlet />
          </div>
        </main>

        {/* Simple Footer */}
        <footer className="p-4 bg-white border-t border-slate-200 text-center text-xs text-slate-400">
          &copy; {new Date().getFullYear()} GlobalEPRC Admin Panel • Connected to Firebase DB
        </footer>
      </div>
    </div>
  );
};

export default AdminLayout;
